import { aggregateMetrics, createEmptyMetrics } from '@shared/config/usageMetricsConfig';
import { formatAccountAge } from '@shared/utils/formatAccountAge';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export function useAdvisorUsageMetrics(
  usageDetails: Record<string, UsageMetrics>,
  getFilteredAdvisorsForUsage: (filterKey?: string | null) => AdvisorMetrics[],
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const usageFilter = searchParams.get('usageFilter');
  const filteredAdvisors = getFilteredAdvisorsForUsage(usageFilter);

  const advisorUsageData = useMemo(
    () =>
      filteredAdvisors.reduce(
        (acc, advisor) => {
          acc[advisor.advisorId] = {
            advisorId: advisor.advisorId,
            advisorName: `${advisor.advisorFirstName} ${advisor.advisorLastName}`,
            advisorEmail: advisor.advisorEmail || '',
            organizationName: advisor.organizationName || '',
            accountAge: formatAccountAge(advisor.advisorCreatedDateTime),
            usageMetrics: usageDetails[advisor.advisorId] || createEmptyMetrics(),
          };
          return acc;
        },
        {} as Record<string, AdvisorUsageData>,
      ),
    [filteredAdvisors, usageDetails],
  );

  const advisorsList = useMemo((): AdvisorUsageData[] => {
    if (!advisorUsageData) {
      return [];
    }
    return Object.values(advisorUsageData);
  }, [advisorUsageData]);

  const activeAdvisorsCount = useMemo(() => {
    return advisorsList.filter(a => a.usageMetrics.totalAdvisorLogins > 0).length;
  }, [advisorsList]);

  const calculatedMetrics = useMemo(() => {
    if (!advisorUsageData) {
      return createEmptyMetrics();
    }
    const advisorsList = Object.values(advisorUsageData) as AdvisorUsageData[];
    return aggregateMetrics(advisorsList.map(a => a.usageMetrics));
  }, [advisorUsageData]);

  return {
    advisorUsageData,
    advisorsList,
    activeAdvisorsCount,
    calculatedMetrics,
  };
}
